import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CartProvider } from './contexts/CartContext';
import { BookingProvider } from './contexts/BookingContext';
import { MaintenanceProvider } from './contexts/MaintenanceContext';

// Hooks
import { useReminders } from './hooks/useReminders';
import { AuthPromptProvider } from './hooks/useAuthPrompt';

// Layouts
import WebsiteLayout from './components/Layout/WebsiteLayout';
import ProfessionalLayout from './components/Layout/ProfessionalLayout';

// Public Pages
import AuthPage from './pages/AuthPage';
import OnboardingProfessional from './pages/OnboardingProfessional';
import OnboardingClient from './pages/OnboardingClient';

// Client Dashboard Pages
import HomePage from './pages/Dashboard/HomePage';
import SearchPage from './pages/Dashboard/SearchPage';
import ProfilePage from './pages/Dashboard/ProfilePage';
import SettingsPage from './pages/Dashboard/SettingsPage';
import ChatPage from './pages/Dashboard/ChatPage';
import CalendarPage from './pages/Dashboard/CalendarPage';
import FavoritesPage from './pages/Dashboard/FavoritesPage';
import ShopPage from './pages/Dashboard/ShopPage';
import ScanPage from './pages/Dashboard/ScanPage';
import DiscoverCategoriesPage from './pages/Dashboard/DiscoverCategoriesPage';
import DiscoverCategoryPage from './pages/Dashboard/DiscoverCategoryPage';
import OrdersPage from './pages/Dashboard/OrdersPage';
import NotificationsPage from './pages/Dashboard/NotificationsPage';
import MapPage from './pages/Dashboard/MapPage';

// Settings sub-pages
import LanguagePage from './pages/Settings/LanguagePage';
import PrivacySecurityPage from './pages/Settings/PrivacySecurityPage';
import PaymentMethodsPage from './pages/Settings/PaymentMethodsPage';
import DataPermissionsPage from './pages/Settings/DataPermissionsPage';
import HelpCenterPage from './pages/Settings/HelpCenterPage';
import PrivacyPolicyPage from './pages/Settings/PrivacyPolicyPage';
import TermsOfServicePage from './pages/Settings/TermsOfServicePage';

// Professional Dashboard Pages
import ProfessionalDashboard from './pages/ProfessionalDashboard';
import ProfessionalServicesPage from './pages/Professional/ProfessionalServicesPage';
import ProfessionalInboxPage from './pages/Professional/ProfessionalInboxPage';
import ProfessionalProfilePage from './pages/Professional/ProfessionalProfilePage';
import ProfessionalBookingsPage from './pages/Professional/ProfessionalBookingsPage';
import ProfessionalProductsPage from './pages/Professional/ProfessionalProductsPage';
import ProfessionalChatPage from './pages/Professional/ProfessionalChatPage';
import ProfessionalOrdersPage from './pages/Professional/ProfessionalOrdersPage';
import ProfessionalNotificationsPage from './pages/Professional/ProfessionalNotificationsPage';

// Professional Client View
import ProfessionalClientView from './pages/Professional/ProfessionalClientView';

// Checkout + Confirmation
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';

// Shared (public)
import SharedCartPage from './pages/SharedCartPage';
import SharedMaintenancePage from './pages/SharedMaintenancePage';
import SharedCheckoutPage from './pages/SharedCheckoutPage';

// Components
import ProtectedRoute from './components/ProtectedRoute';

function ReminderRunner() {
  useReminders();
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading Skytone...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <LocationProvider>
        <NotificationProvider>
          <CartProvider>
            <BookingProvider>
              <MaintenanceProvider>
                <Router>
                  <AuthPromptProvider>
                    <ReminderRunner />

                    <Routes>
                      {/* ============================================================ */}
                      {/* HOME                                                         */}
                      {/* ============================================================ */}
                      <Route path="/" element={
                        <WebsiteLayout>
                          <HomePage />
                        </WebsiteLayout>
                      } />

                      {/* ============================================================ */}
                      {/* AUTH — Let AuthPage handle role-based redirects              */}
                      {/* ============================================================ */}
                      <Route path="/login" element={<AuthPage />} />
                      <Route path="/register" element={<AuthPage />} />

                      {/* ============================================================ */}
                      {/* SHARED (PUBLIC)                                              */}
                      {/* ============================================================ */}
                      <Route path="/cart/:token" element={<SharedCartPage />} />
                      <Route path="/maintenance/:uid/:scheduleId" element={<SharedMaintenancePage />} />
                      <Route path="/checkout-share/:token" element={<SharedCheckoutPage />} />

                      {/* ============================================================ */}
                      {/* ONBOARDING                                                   */}
                      {/* ============================================================ */}
                      <Route path="/onboarding-client" element={
                        <ProtectedRoute>
                          <OnboardingClient />
                        </ProtectedRoute>
                      } />
                      <Route path="/onboarding-professional" element={
                        <ProtectedRoute>
                          <OnboardingProfessional />
                        </ProtectedRoute>
                      } />

                      {/* ============================================================ */}
                      {/* PUBLIC BROWSING — no ProtectedRoute                          */}
                      {/* ============================================================ */}
                      <Route path="/dashboard" element={
                        <WebsiteLayout>
                          <HomePage />
                        </WebsiteLayout>
                      } />

                      <Route path="/dashboard/search" element={
                        <WebsiteLayout>
                          <SearchPage />
                        </WebsiteLayout>
                      } />

                      <Route path="/dashboard/discover" element={
                        <WebsiteLayout>
                          <DiscoverCategoriesPage />
                        </WebsiteLayout>
                      } />

                      <Route path="/dashboard/discover/:category" element={
                        <WebsiteLayout>
                          <DiscoverCategoryPage />
                        </WebsiteLayout>
                      } />

                      <Route path="/dashboard/shop" element={
                        <WebsiteLayout>
                          <ShopPage />
                        </WebsiteLayout>
                      } />

                      <Route path="/dashboard/calendar" element={
                        <WebsiteLayout>
                          <CalendarPage />
                        </WebsiteLayout>
                      } />

                      <Route path="/dashboard/chat" element={
                        <WebsiteLayout>
                          <ChatPage />
                        </WebsiteLayout>
                      } />

                      <Route path="/dashboard/map" element={
                        <WebsiteLayout>
                          <MapPage />
                        </WebsiteLayout>
                      } />

                      <Route path="/professional/:id" element={
                        <WebsiteLayout>
                          <ProfessionalClientView />
                        </WebsiteLayout>
                      } />

                      {/* ============================================================ */}
                      {/* AUTH-ONLY CLIENT PAGES                                       */}
                      {/* ============================================================ */}
                      <Route path="/dashboard/notifications" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <NotificationsPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/scan" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <ScanPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/favorites" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <FavoritesPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/orders" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <OrdersPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/profile" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <ProfilePage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/settings" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <SettingsPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      {/* ============================================================ */}
                      {/* SETTINGS SUB-PAGES                                           */}
                      {/* ============================================================ */}
                      <Route path="/dashboard/settings/language" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <LanguagePage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/settings/privacy" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <PrivacySecurityPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/settings/payment-methods" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <PaymentMethodsPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/settings/data" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <DataPermissionsPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/settings/help" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <HelpCenterPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/settings/privacy-policy" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <PrivacyPolicyPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/dashboard/settings/terms" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <TermsOfServicePage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      {/* ============================================================ */}
                      {/* CHECKOUT + CONFIRMATION                                      */}
                      {/* ============================================================ */}
                      <Route path="/checkout" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <CheckoutPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/order-confirmation/:orderId" element={
                        <ProtectedRoute>
                          <WebsiteLayout>
                            <OrderConfirmationPage />
                          </WebsiteLayout>
                        </ProtectedRoute>
                      } />

                      {/* ============================================================ */}
                      {/* PROFESSIONAL DASHBOARD                                       */}
                      {/* ============================================================ */}
                      <Route path="/professional-dashboard" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalDashboard />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/services" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalServicesPage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/bookings" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalBookingsPage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/products" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalProductsPage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/orders" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalOrdersPage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/notifications" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalNotificationsPage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/inbox" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalInboxPage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/inbox/:clientId" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalChatPage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/professional-dashboard/profile" element={
                        <ProtectedRoute>
                          <ProfessionalLayout>
                            <ProfessionalProfilePage />
                          </ProfessionalLayout>
                        </ProtectedRoute>
                      } />

                      {/* ============================================================ */}
                      {/* CATCH ALL                                                    */}
                      {/* ============================================================ */}
                      <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                  </AuthPromptProvider>
                </Router>
              </MaintenanceProvider>
            </BookingProvider>
          </CartProvider>
        </NotificationProvider>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;